import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useEffect, useState, type ReactNode } from 'react';
import { Button, EmptyState, ErrorState } from '@bb/ui';
import { ApiError, type CanvasListFilters, type CanvasSummary } from '../../api/client.ts';
import { Can } from '../access/Can.tsx';
import { formatCount } from '../audience/ChannelReachBreakdown.tsx';
import { ColumnsPicker, type ListColumn } from '../lists/ColumnsPicker.tsx';
import { IdleBanner } from '../lists/IdleBanner.tsx';
import { formatWhen } from '../segments/SegmentsListPage.tsx';
import { CanvasActions } from './CanvasActions.tsx';
import { CanvasStatusBadge } from './CanvasStatusBadge.tsx';
import { CreateCanvasDialog } from './CreateCanvasDialog.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
export const ENTRY_LABELS: Record<NonNullable<CanvasSummary['entryType']>, string> = { scheduled: 'Scheduled', action_based: 'Action-based', api_triggered: 'API-triggered' };
const COLUMNS_PREFERENCE = 'canvas.list.columns';
const COLUMNS = [
  { id: 'entryType', label: 'Entry type' },
  { id: 'sent', label: 'Sent' },
  { id: 'inProgress', label: 'In progress' },
  { id: 'failed', label: 'Failed entries' },
  { id: 'tags', label: 'Tags' },
  { id: 'updated', label: 'Last edited' },
  { id: 'stopped', label: 'Stop date' },
  { id: 'alerts', label: 'Alerts configured' },
] as const satisfies readonly ListColumn<string>[];
type Column = (typeof COLUMNS)[number]['id'];
const DEFAULT_COLUMNS: Column[] = ['entryType', 'sent', 'inProgress', 'tags', 'updated'];
const STATUS_OPTIONS: Array<[NonNullable<CanvasListFilters['status']> | '', string]> = [
  ['', 'All but archived'],
  ['draft', 'Draft'],
  ['active', 'Active'],
  ['idle', 'Idle'],
  ['paused', 'Paused'],
  ['stopped', 'Stopped'],
  ['archived', 'Archived'],
  ['all', 'All'],
];

const CELLS: Record<Column, (c: CanvasSummary) => ReactNode> = {
  entryType: (c) => (c.entryType ? ENTRY_LABELS[c.entryType] : '—'),
  sent: (c) => formatCount(c.sent),
  inProgress: (c) => formatCount(c.activeEntries),
  failed: (c) => (c.failedEntries ? <span className="tag--warning">{formatCount(c.failedEntries)}</span> : '0'),
  tags: (c) =>
    c.tags.map((t) => (
      <span key={t.id} className="tag">
        {t.name}
      </span>
    )),
  updated: (c) => `${formatWhen(c.updatedAt)}${c.updatedBy?.email ? ` by ${c.updatedBy.email}` : ''}`,
  stopped: (c) => (c.stoppedAt ? formatWhen(c.stoppedAt) : '—'),
  alerts: (c) => (c.alertsConfigured ? 'Yes' : 'No'),
};

/**
 * Messaging → Canvas (BB-109): status (with Idle), entry type, tags and search; entries in progress and failed for
 * operations; optional columns saved per employee; create from a template, duplicate, archive and restore.
 */
export function CanvasListPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/canvas' });
  const { api } = useRouteContext({ from: '/env/$environmentId/canvas' });
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);
  const [status, setStatus] = useState<CanvasListFilters['status'] | ''>('');
  const [entryType, setEntryType] = useState<CanvasListFilters['entryType'] | ''>('');
  const [tagId, setTagId] = useState('');
  const [sort, setSort] = useState<NonNullable<CanvasListFilters['sort']>>('updated');
  const [creating, setCreating] = useState(false);
  const filters: CanvasListFilters = { sort, ...(query ? { query } : {}), ...(status ? { status } : {}), ...(entryType ? { entryType } : {}), ...(tagId ? { tagId } : {}) };
  const list = useInfiniteQuery({
    queryKey: ['canvases', environmentId, filters],
    queryFn: ({ pageParam }) => api.listCanvases(environmentId, { ...filters, ...(pageParam ? { cursor: pageParam } : {}) }),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const tags = useQuery({ queryKey: ['tags', environmentId], queryFn: () => api.listTags(environmentId) });
  const saved = useQuery({ queryKey: ['preference', COLUMNS_PREFERENCE], queryFn: () => api.getPreference<Column[]>(COLUMNS_PREFERENCE) });
  const known = new Set<string>(COLUMNS.map((c) => c.id));
  const columns = Array.isArray(saved.data?.value) ? saved.data.value.filter((c) => known.has(c)) : DEFAULT_COLUMNS;
  const saveColumns = useMutation({
    mutationFn: (next: Column[]) => api.putPreference(COLUMNS_PREFERENCE, next),
    onMutate: (next) => queryClient.setQueryData(['preference', COLUMNS_PREFERENCE], { key: COLUMNS_PREFERENCE, value: next }),
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const first = list.data?.pages[0];
  const filtered = Boolean(query || status || entryType || tagId);

  return (
    <section>
      <h1>Canvas</h1>
      <p className="hint">Multi-step journeys. A running Canvas keeps its published version; entries in progress and failed entries show what needs attention.</p>
      <Can permission="canvas.edit">
        <Button variant="primary" onClick={() => setCreating(true)}>
          Create Canvas
        </Button>
      </Can>
      {first && status !== 'idle' && <IdleBanner count={first.idleCount} idleDays={first.idleDays} noun={['Canvas', 'Canvases']} onShow={() => setStatus('idle')} />}
      <div className="filters">
        <input aria-label="Search Canvas" placeholder="Search by name or API id" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
          {STATUS_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select aria-label="Filter by entry type" value={entryType} onChange={(e) => setEntryType(e.target.value as typeof entryType)}>
          <option value="">All entry types</option>
          {Object.entries(ENTRY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select aria-label="Filter by tag" value={tagId} onChange={(e) => setTagId(e.target.value)}>
          <option value="">All tags</option>
          {(tags.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <select aria-label="Sort by" value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
          <option value="updated">Last edited</option>
          <option value="created">Created</option>
          <option value="name">Name</option>
        </select>
        <ColumnsPicker columns={COLUMNS} value={columns} onChange={(next) => saveColumns.mutate(next)} />
      </div>
      {list.isPending ? (
        <p aria-busy="true">Loading Canvas…</p>
      ) : list.isError ? (
        <ErrorState title="Canvas could not be loaded" message={errorText(list.error)} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={filtered ? 'No matching Canvas' : 'No Canvas yet'} description={filtered ? 'Nothing matches the filters.' : 'Create the first Canvas to build a journey.'} />
      ) : (
        <>
          <table className="table" aria-label="Canvas">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Status</th>
                {columns.map((id) => (
                  <th key={id} scope="col">
                    {COLUMNS.find((c) => c.id === id)!.label}
                  </th>
                ))}
                <th scope="col">
                  <span className="bb-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to="/env/$environmentId/canvas/$canvasId" params={{ environmentId, canvasId: c.id }}>
                      {c.name}
                    </Link>
                  </td>
                  <td>
                    <CanvasStatusBadge canvas={c} />
                  </td>
                  {columns.map((id) => (
                    <td key={id}>{CELLS[id](c)}</td>
                  ))}
                  <td>
                    <CanvasActions environmentId={environmentId} canvas={c} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {list.hasNextPage && (
            <Button variant="ghost" onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
              Load more
            </Button>
          )}
        </>
      )}
      <CreateCanvasDialog environmentId={environmentId} open={creating} onOpenChange={setCreating} />
    </section>
  );
}
